import { useState, useEffect, useRef } from "react";
import { FilterState, SortDirection, SortState, TableRow } from "../../types/types.table";

interface UseTableDataProps {
  loadData: (params: {
    filters: Record<any, any>;
    sort: { column: string | null; direction: SortDirection };
    pagination: { page: number; limit: number };
  }) => Promise<{ data: TableRow[]; total: number }>;
  externalFilters?: Record<string, string>;
}

export function useTableData(props: UseTableDataProps) {
  const [data, setData] = useState<TableRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [sortState, setSortState] = useState<SortState>({
    column: "id",
    direction: "asc",
  });

  const [filters, setFilters] = useState<FilterState>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(
    +(localStorage.getItem("perPage") || 10)
  );

  const loadData = async () => {
    setLoading(true);

    try {
      const params = {
        filters: { ...filters, ...props.externalFilters },
        sort: sortState,
        pagination: {
          page: currentPage,
          limit: rowsPerPage,
        },
      };

      const result = await props.loadData(params);

      setData(result.data || []);
      setTotalItems(result.total || 0);
    } catch (err) {
      console.error("loadData error:", err);
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters, sortState, currentPage, rowsPerPage, props.externalFilters]);

  const handleSort = (column: string) => {
    setSortState(prev =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" }
    );
  };

  const applyFilters = (newFilters: FilterState) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    setCurrentPage(1);
  };

  return {
    data,
    totalItems,
    loading,
    sortState,
    setSortState,
    filters,
    setFilters,
    applyFilters,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    loadData,
    handleSort,
  };
}